import { z } from 'zod';
import { boundedDateRange } from './payroll.schema.js';
import { WorkforceGroupQuerySchema, type WorkforceGroup } from '../filters/workforceGroup.js';
import { CorrectionTypeSchema, type CorrectionType } from './correctionType.schema.js';

export const EventTypeSchema = z.enum(['PUNCH_IN', 'PUNCH_OUT', 'BREAK_START', 'BREAK_END']);

export const PunchRequestSchema = z.object({
  // Optional: a device with no GPS may still punch at a worksite that allows
  // remote punch (skip geofence) or under the gps_anywhere policy. The server
  // enforces "coords required" per worksite/policy. When present they're
  // recorded and geofenced as before.
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  accuracy: z.number().nonnegative().optional(),
  isMockLocation: z.boolean().optional(),
  clientTimestamp: z.string().datetime().optional(),
  idempotencyKey: z.string().min(8).max(80).optional(),
  notes: z.string().max(500).optional(),
  /**
   * Optional selfie capture, base64-encoded JPEG. Required when
   * settings.attendance.requirePhoto.web === true. Frontend downscales
   * to ~480x360 before encoding so the body stays well under the json
   * 1MB limit.
   */
  photoBase64: z.string().optional().nullable(),
  /**
   * Optional 192-d MobileFaceNet face template (base64 little-endian
   * float32[192] = 1024 chars) computed from the SAME captured frame as
   * photoBase64. Used ONLY when settings.attendance.requirePhoto.web is on:
   * the server verifies it against the employee's enrolled template (or
   * self-enrolls it on the first punch). Kept a loose string on the wire —
   * NOT FieldTemplateB64Schema — so a malformed capture can never 400/block a
   * punch; the server decodes it defensively (decodeTemplateB64) and, per the
   * "flag, don't block" policy, simply skips verification when it can't decode.
   */
  faceTemplate: z.string().optional().nullable(),
  /**
   * WHICH of the sites in range the worker says they are at.
   *
   * A NARROWING, NEVER A CLAIM. The server still computes the in-range set from
   * the coordinates in this same request, and accepts this id only if it is a
   * MEMBER of that set — so it can move a punch between two sites the worker is
   * demonstrably standing in, and can never name a site they are nowhere near.
   * A worker who cannot fake their GPS cannot fake their choice.
   *
   * Absent means "you decide", which is the old behaviour exactly: the nearest
   * site in range. Every punch made before this field existed keeps its
   * attribution.
   */
  worksiteId: z.string().min(1).optional(),
});
export type PunchRequest = z.infer<typeof PunchRequestSchema>;

export const ManualPunchSchema = z.object({
  employeeId: z.string().min(1),
  type: EventTypeSchema,
  serverTimestamp: z.string().datetime(),
  worksiteId: z.string().optional().nullable(),
  notes: z.string().max(500).optional().nullable(),
  idempotencyKey: z.string().min(8).max(80).optional(), // minted when the form opens: a double submit is one punch (M-03)
});
export type ManualPunch = z.infer<typeof ManualPunchSchema>;

export { CorrectionTypeSchema, type CorrectionType } from './correctionType.schema.js';

/** The correction types a web user may submit. VOID_PUNCH is device-only. */
export const WebCorrectionTypeSchema = CorrectionTypeSchema.exclude(['VOID_PUNCH'], {
  errorMap: () => ({
    message:
      'requestType must be MISSED_PUNCH_IN, MISSED_PUNCH_OUT, WRONG_TIME, ADD_BREAK or WRONG_DIRECTION ' +
      '(VOID_PUNCH can only be raised from a field device)',
  }),
});
export type WebCorrectionType = z.infer<typeof WebCorrectionTypeSchema>;

/**
 * Best-guess correction type for a given AttendanceDay status, used to
 * pre-select the dropdown when a user deep-links from "My attendance" into the
 * correction form. It's only a default — the user can change it — so the
 * mapping favours the most common fix for each status:
 *   - ABSENT / WEEKEND / HOLIDAY / ON_LEAVE: a missed punch-in is the usual
 *     reason a worked day shows up as non-working / empty.
 *   - INCOMPLETE: a punch-in with no punch-out → missed punch-out.
 *   - everything else (PRESENT / LATE / HALF_DAY): a wrong recorded time.
 * Unknown / undefined statuses fall back to MISSED_PUNCH_IN.
 */
export function suggestedCorrectionType(status: string | undefined): CorrectionType {
  switch (status) {
    case 'INCOMPLETE':
      return 'MISSED_PUNCH_OUT';
    case 'PRESENT':
    case 'LATE':
    case 'HALF_DAY':
      return 'WRONG_TIME';
    case 'ABSENT':
    case 'WEEKEND':
    case 'HOLIDAY':
    case 'ON_LEAVE':
    default:
      return 'MISSED_PUNCH_IN';
  }
}

export const CorrectionRequestInputSchema = z.object({
  date: z.string().date(),
  requestType: WebCorrectionTypeSchema,
  requestedTime: z.string().datetime(),
  reason: z.string().min(5).max(2000),
  /**
   * The exact AttendanceEvent a WRONG_TIME correction supersedes. Optional
   * because the requester may not know the event id; when omitted the approver
   * can only proceed if the target day carries a single, unambiguous punch —
   * otherwise the approval is refused rather than guessing which punch to void.
   * Ignored for types that add a new event; REQUIRED for WRONG_DIRECTION. */
  targetEventId: z.string().min(1).optional(),
  idempotencyKey: z.string().min(8).max(80).optional(), // minted when the form opens: a double submit is one correction (M-03)
}); // a WRONG_DIRECTION with no targetEventId is refused by the service: a refine would break `.extend`
export type CorrectionRequestInput = z.infer<typeof CorrectionRequestInputSchema>;

export const CorrectionDecisionSchema = z.object({ rejectionReason: z.string().max(500).optional().nullable() });
export type CorrectionDecision = z.infer<typeof CorrectionDecisionSchema>;

/**
 * Manager / HR / Admin override of a flagged attendance event. Only the
 * two human-decidable terminal states are allowed: VALID ("flag was a
 * false alarm, accept the punch") or REJECTED ("flag was real, drop the
 * punch from the rollup"). FLAGGED and PENDING_APPROVAL are entered by
 * the system, not by humans, so they're not part of this schema.
 *
 * `reason` is required (≥ 5 chars) so the audit trail captures why the
 * override happened — silent overrides defeat the purpose of FLAGGED.
 */
export const EventStatusOverrideSchema = z.object({
  status: z.enum(['VALID', 'REJECTED']),
  reason: z.string().min(5).max(500),
});
export type EventStatusOverride = z.infer<typeof EventStatusOverrideSchema>;

/**
 * THE SAME DECISION, OVER A SET. `status` and `reason` are deliberately the
 * identical fields with the identical bounds as the single-event schema above —
 * a bulk press is the same decision applied to a day's worth of scans at once,
 * and a reason good enough for one punch is what the audit wants on all four.
 *
 * The cap is the queue page size: a page is the most an approver can have in
 * front of them, so it is the most they can decide in one press.
 *
 * No `closeSiteDay` (retired 2026-11-03): an approved camp check-out closes the
 * visit itself, so there is nothing left to close. An older client that still
 * sends it is not refused — undeclared keys are stripped, so it is ignored.
 */
export const BulkEventStatusOverrideSchema = z.object({
  eventIds: z.array(z.string().min(1)).min(1).max(100),
  status: z.enum(['VALID', 'REJECTED']),
  reason: z.string().min(5).max(500),
});
export type BulkEventStatusOverride = z.infer<typeof BulkEventStatusOverrideSchema>;

/** Query for the per-scan flagged queue. */
export { FlaggedScanQuerySchema, type FlaggedScanQuery } from './flaggedScanQuery.schema.js';

/**
 * Range cap for the per-employee day/event feeds (`GET /attendance/days`,
 * `GET /attendance/events`) — TIGHTER than the report cap, and deliberately the
 * same number the muster roll already enforces
 * (`CALENDAR_MAX_RANGE_DAYS = 92` in attendanceCalendar.service.ts). It is the
 * same data seen one employee at a time; two caps over one dataset that disagree
 * is how "92 days" becomes folklore instead of a rule.
 *
 * WHY 92 IS ABOVE EVERY REAL FLOW (UI audit, 2026-08-15, apps/web — every caller
 * of `attendanceApi.days` / `.events`):
 *   - `routes/attendance/MyAttendancePage.tsx` — defaults to the current
 *     calendar month and its month field always lands on a WHOLE month
 *     (`monthRangeOf` → `monthRange`). Real ceiling: 31 days.
 *   - `routes/employees/WorkerAttendanceHistoryPage.tsx` — defaults to the
 *     trailing 30 days; issues BOTH the days and the events query for one worker.
 *   - `routes/attendance/CorrectionsPage.tsx` — a single day (`from === to`).
 * So the widest range any of them produces is 31 days and the cap is a full
 * quarter — three times the ceiling, and the longest window anyone has a
 * legitimate reason to render as a list. The free date inputs on the first two
 * pages are clamped to it client-side, so the 422 is a backstop for hand-written
 * URLs rather than something a user can reach through the app.
 *
 * WHY TIGHTER THAN THE REPORTS' 366 AT ALL: `listEvents` is a 26-field select
 * plus a nested worksite select, ordered desc, with no `take`. A punch feed is
 * read to check a payslip or settle a dispute about a shift — questions asked
 * about a month, never about a decade.
 */
export const MAX_ATTENDANCE_RANGE_DAYS = 92;

export const AttendanceDayQuerySchema = z.object({
  employeeId: z.string().optional(),
  from: z.string().date(),
  to: z.string().date(),
  /**
   * `?includeTotals=true` asks GET /attendance/days for the `meta.totals`
   * envelope — the Worked / Late / Overtime / Absent strip an employee checks
   * their payslip against on "My attendance".
   *
   * It had to be declared here to work at all: `validate` overwrites req.query
   * with the Zod-parsed object and Zod strips unknown keys, so the client sent
   * it (apps/web/src/api/attendance.api.ts `daysWithTotals`), the route looked
   * for it, the service that computes the totals was fully written — and the
   * branch had never once executed. The strip is silent, so nothing anywhere
   * reported a problem; the strip just read as "the backend omits meta for
   * empty ranges".
   *
   * A loose string rather than a boolean: it arrives as a query string, and the
   * route compares it to the literal 'true'. Optional, so every other caller of
   * this schema (/attendance/events, timesheet) is unaffected.
   */
  includeTotals: z.string().optional(),
}).superRefine(boundedDateRange(MAX_ATTENDANCE_RANGE_DAYS));
export type AttendanceDayQuery = z.infer<typeof AttendanceDayQuerySchema>;

/**
 * GET /attendance/record — one worker, one day, with the evidence behind it.
 *
 * A SINGLE day rather than a range, and that is the design rather than a
 * limitation. This payload carries the coordinates, the face-match scores and
 * the per-punch evidence for every punch, and it is read when somebody is
 * settling one disputed morning. A range parameter would turn the screen that
 * answers "was he here at ten to seven" into a bulk export of biometric
 * evidence, which is a different feature with a different risk profile.
 */
export const AttendanceRecordQuerySchema = z.object({
  employeeId: z.string().optional(),
  /** A calendar day, resolved against the COMPANY timezone by the route. */
  date: z.string().date(),
});
export type AttendanceRecordQuery = z.infer<typeof AttendanceRecordQuerySchema>;

// ---------------------------------------------------------------------------
// Attendance calendar (the muster roll)
// ---------------------------------------------------------------------------

/**
 * WHICH PART OF THE WORKFORCE THE CALENDAR IS SHOWING — the app's one
 * workforce group (filters/workforceGroup.ts, owner D1): Workers · Staff ·
 * Remote split everyone cleanly, so the three add up to the roll. Until M5
 * the calendar's own `staff | labour | remote` were overlapping presets (a
 * remote office worker was in Staff AND Remote); `labour` is still read, as
 * Workers, so an old link opens the same group.
 */
export const CalendarGroupSchema = WorkforceGroupQuerySchema;
export type CalendarGroup = WorkforceGroup;
/** "Had at least one such day in the range" — see attendanceCalendar.statusFilter.ts. */
export const CalendarStatusFilterSchema = z.enum(['attended', 'late', 'absent', 'onLeave', 'unresolved']);
export type CalendarStatusFilter = z.infer<typeof CalendarStatusFilterSchema>;

/**
 * Rows per page.
 *
 * 50 is what the day roster already paged at client-side (`DAY_PAGE_SIZE`) and
 * is a screenful and a half; it is now the size BOTH views ask the server for,
 * so the month grid and the day list turn pages of the same population.
 *
 * The maximum is the real protection. Every row carries up to
 * `CALENDAR_MAX_RANGE_DAYS` (92) cells, so the payload grows as pageSize x
 * range: 200 x 92 is ~18k cells, which is the most this endpoint will ever be
 * asked to serialise. It replaces the old 1000-employee row cap, which capped
 * the same product at five times that and then had to apologise for it.
 */
export const CALENDAR_DEFAULT_PAGE_SIZE = 50;
export const CALENDAR_MAX_PAGE_SIZE = 200;

export const AttendanceCalendarQuerySchema = z.object({
  from: z.string().date(),
  to: z.string().date(),
  group: CalendarGroupSchema.optional(),
  departmentId: z.string().optional(),
  worksiteId: z.string().optional(),
  /** Free text over employee name and code. */
  search: z.string().max(120).optional(),
  status: CalendarStatusFilterSchema.optional(),
  sort: z.enum(['name', 'latest']).optional(), // latest: newest movement first, one day only
  /** The whole filtered set as a file (merge E5): one day → the day roster, a range → the grid. */
  format: z.enum(['excel', 'csv']).optional(),
  /**
   * 1-BASED PAGE, matching `PageQuery` in worksiteTime.routes.ts and the
   * `ListQuerySchema` in deviceApprovals.routes.ts (the older `PaginationSchema`
   * says `limit`; the newer pair won, so a client need not remember families).
   *
   * `z.coerce` because it arrives as a query string. A page past the end is
   * CLAMPED by the service rather than rejected: an out-of-date bookmark should
   * land on rows, not on a 422 or a screen that reads as "everybody vanished". */
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce
    .number()
    .int()
    .positive()
    .max(CALENDAR_MAX_PAGE_SIZE)
    .default(CALENDAR_DEFAULT_PAGE_SIZE),
});
export type AttendanceCalendarQuery = z.infer<typeof AttendanceCalendarQuerySchema>;
/**
 * What a CALLER may send: `page`/`pageSize` have defaults, so they are optional
 * on the wire even though the parsed type has them. Used by the web client so it
 * can omit them.
 */
export type AttendanceCalendarQueryInput = z.input<typeof AttendanceCalendarQuerySchema>;

/** One employee's day. Absent from the array means NO RECORD, which is not the
 *  same as ABSENT — the grid draws nothing rather than an accusation. */
export interface AttendanceCalendarCell {
  /** YYYY-MM-DD. */
  date: string;
  status: string;
  /** ISO instants of the day's first in and last out; null when unpunched. */
  firstPunchIn: string | null;
  lastPunchOut: string | null;
  workedMinutes: number;
  lateMinutes: number;
  overtimeMinutes: number;
  isHoliday: boolean;
  isWeekend: boolean;
  isOnLeave: boolean;
}

export interface AttendanceCalendarRow {
  employeeId: string;
  employeeCode: string;
  name: string;
  department: string | null;
  designation: string | null;
  workforceType: 'SITE_WORKER' | 'OFFICE_STAFF';
  attendanceLocation: 'ASSIGNED_SITE' | 'ANY_WORKSITE' | 'ANYWHERE';
  /** Sparse and ascending by date. */
  cells: AttendanceCalendarCell[];
  totals: {
    present: number;
    late: number;
    absent: number;
    onLeave: number;
    holiday: number;
    weekend: number;
    workedMinutes: number;
    overtimeMinutes: number;
  };
}

/**
 * One column's footer. The buckets are EXHAUSTIVE and mutually exclusive except for `late`:
 *
 *     present + absent + onLeave + holiday + weekend + noRecord === rows.length
 *
 * `late` is a SUBSET of `present`, matching the site manpower board — a late
 * arrival is somebody who came, and counting them apart from `present` would
 * make the two screens disagree about the same morning. It is therefore the one
 * field you must not add into the identity above.
 *
 * `holiday` and `weekend` exist precisely so that identity holds. Without them a
 * rest day's cells fall into no bucket at all while still having a record, so
 * the footer silently under-counts and the only way to make the arithmetic work
 * is to shovel the shortfall into `absent` — which would accuse a whole
 * workforce of not turning up on a Friday.
 */
export type AttendanceCalendarRestDay = 'WEEKEND' | 'HOLIDAY';
export interface AttendanceCalendarDayTotal {
  date: string;
  restDay: AttendanceCalendarRestDay | null; // a COMPANY rest day (≤ today) whoever is in scope: a filter matching nobody is not the cause
  present: number;
  late: number;
  absent: number;
  onLeave: number;
  holiday: number;
  weekend: number;
  /** Employees in the filtered set with no record at all for this day. */
  noRecord: number;
  /**
   * How many of them have a recorded first punch on this day. NOT part of the
   * partition above — it is a fact about the clock, not a bucket — and it is
   * deliberately not the same number as `present`: `present` also counts a
   * status the rollup wrote without a punch (a half day approved by hand), and
   * "checked in" is a claim only a punch can support.
   *
   * It exists because the month view's Today strip has to say "N of M checked
   * in" over the WHOLE filtered workforce. It used to count the roster entries
   * on screen, which was the same number only while the screen held everybody.
   */
  checkedIn: number;
  checkedOut: number; // a recorded last punch OUT; same scope and rule as checkedIn
  /**
   * DAYS THAT WILL NOT REACH PAYROLL. A day punched into and never out of is
   * force-closed at local midnight, so its minutes are manufactured and the ERP
   * export refuses them (`utils/payableDay.ts`). That count existed already and
   * was rendered on ONE screen — the export screen, the last place anyone can
   * still act on it. Stated here on the month a supervisor reviews instead.
   * NOT part of the partition: these sit inside `present` (the person was at
   * the gate) and this says which cannot be priced. A subset, like `late`.
   */
  unresolved: number;
}

/** One site a person was recorded at on the roster day, with their clock there. */
export interface AttendanceCalendarRosterSite {
  worksiteName: string | null;
  firstInAt: string | null;
  lastOutAt: string | null;
}

/**
 * One person on the roster day.
 *
 * EVERY employee in the grid gets an entry, including those with no
 * AttendanceDay row — that is the whole point. The grid draws a missing row as
 * an empty cell, correctly, because "no record" is not "absent" (see the header
 * comment in attendanceCalendar.service.ts). But at 2pm on a working day an
 * empty column answers nothing, and the question being asked is "what is
 * happening today". So the roster lists the person and says plainly that they
 * have not checked in yet, which is a fact, rather than leaving a blank that
 * could mean anything.
 */
export interface AttendanceCalendarRosterEntry {
  employeeId: string;
  employeeCode: string;
  name: string;
  department: string | null;
  /** The day's status, or 'NO_RECORD' when the rollup has written nothing yet. */
  status: string;
  firstPunchIn: string | null;
  lastPunchOut: string | null;
  workedMinutes: number | null;
  /** Where they were recorded. Empty when no site was attributed. */
  sites: AttendanceCalendarRosterSite[];
}

/**
 * WHICH SLICE OF THE FILTERED WORKFORCE `rows` IS.
 *
 * `total` is the headcount of the WHOLE filtered scope, not of this page, and it
 * is the number every "of N matching" caption on both views is drawn from. It
 * replaces the old `matched` (and the `truncated` flag beside it): with real
 * paging there is no longer a set of people the client cannot reach, so a
 * boolean saying "we did not fetch everybody" would have nothing left to warn
 * about. What it warned about is now simply `pageCount > 1`.
 */
export interface AttendanceCalendarMeta {
  /** 1-based, and CLAMPED — ask for page 99 of 3 and this says 3. */
  page: number;
  pageSize: number;
  /** Employees matching the whole filtered scope, across every page. */
  total: number;
  /** At least 1, even for an empty scope — "page 1 of 1, with nobody on it". */
  pageCount: number;
}

export interface AttendanceCalendarPayload {
  /** Every date in [from..to], ascending — the grid's columns. */
  days: string[];
  /** ONE PAGE of employees — see `meta`. */
  rows: AttendanceCalendarRow[];
  /**
   * Column footers: the shape of each day across the ENTIRE filtered workforce,
   * not across `rows`.
   *
   * THIS IS THE LOAD-BEARING ASYMMETRY OF THIS PAYLOAD. Rows are paged;
   * aggregates are scope-wide. If these were computed over the page, "absent"
   * would silently come to mean "absent among the fifty people currently
   * visible" — a confident number that is wrong, with nothing on screen saying
   * so. They are therefore computed by a separate GROUP BY over the same
   * `where` the rows are drawn from, and they are IDENTICAL on every page.
   */
  dayTotals: AttendanceCalendarDayTotal[];
  /** Which page `rows` is, and how many people the aggregates speak for. */
  meta: AttendanceCalendarMeta;
  /**
   * Per-person detail for a single day — present when the request names
   * exactly one day (`from === to`, the day-detail page's own shape), or when
   * TODAY falls inside a wider range (the month grid, which only ever wants
   * today's column of this). Null for a multi-day range that does not include
   * today, which is the case the grid serves worst.
   *
   * ROW DATA, SO IT IS PAGED WITH THE ROWS: it carries one entry per employee
   * in `rows` and nothing about the people on other pages. Anything that needs
   * to speak for the whole scope (the Today strip's "N of M checked in") reads
   * `dayTotals[].checkedIn` and `meta.total` instead.
   */
  dayRoster: {
    /** YYYY-MM-DD. */
    date: string;
    entries: AttendanceCalendarRosterEntry[];
  } | null;
}
